/**
 * Email Service using Firebase Cloud Functions
 * Handles all email sending functionality via serverless functions.
 * Uses callable (sendEmailCallable) when available to avoid CORS from custom domains (e.g. pos.kingspark.co.nz).
 */

import { config } from './config';
import { getFirebaseFunctions } from './firebase';
import { httpsCallable } from 'firebase/functions';

// Get Firebase function URL
const getFunctionUrl = () => {
  // In production, use the deployed function URL
  // In development, you can use the emulator or deployed URL
  const projectId = config.firebase.projectId;
  const region = 'us-central1'; // Default region, change if you deploy to a different region
  
  if (config.isDevelopment) {
    // For local development with emulator
    // return 'http://localhost:5001/kingsparkpos/us-central1/sendEmail';
    // For development, use the deployed function
    return `https://${region}-${projectId}.cloudfunctions.net/sendEmail`;
  }
  
  return `https://${region}-${projectId}.cloudfunctions.net/sendEmail`;
};

// Email templates
const emailTemplates = {
  /**
   * Staff invitation email template
   */
  staffInvitation: (staffName: string, resetLink: string) => ({
    subject: 'Welcome to Kings Park POS - Set Up Your Account',
    html: `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Welcome to Kings Park POS</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #ec4899 0%, #db2777 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">KINGS PARK POS</h1>
            <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0 0;">Restaurant Point of Sale System</p>
          </div>
          
          <div style="background: #ffffff; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 10px 10px;">
            <h2 style="color: #111827; margin-top: 0;">Welcome, ${staffName}!</h2>
            
            <p style="color: #4b5563; font-size: 16px;">
              You've been added to the Kings Park POS system. To get started, please set up your account by creating a password.
            </p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${resetLink}" 
                 style="display: inline-block; background: #ec4899; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;">
                Set Up Your Account
              </a>
            </div>
            
            <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
              This link will expire in 24 hours. If you didn't expect this email, please contact your administrator.
            </p>
            
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
            
            <p style="color: #9ca3af; font-size: 12px; margin: 0;">
              If the button doesn't work, copy and paste this link into your browser:<br>
              <a href="${resetLink}" style="color: #ec4899; word-break: break-all;">${resetLink}</a>
            </p>
          </div>
          
          <div style="text-align: center; margin-top: 20px; color: #9ca3af; font-size: 12px;">
            <p>© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.</p>
          </div>
        </body>
      </html>
    `,
    text: `
Welcome to Kings Park POS!

Hi ${staffName},

You've been added to the Kings Park POS system. To get started, please set up your account by creating a password.

Click this link to set up your account:
${resetLink}

This link will expire in 24 hours. If you didn't expect this email, please contact your administrator.

© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.
    `.trim(),
  }),

  /**
   * Password reset email template
   */
  passwordReset: (resetLink: string) => ({
    subject: 'Reset Your Kings Park POS Password',
    html: `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Reset Your Password</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #ec4899 0%, #db2777 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">KINGS PARK POS</h1>
            <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0 0;">Password Reset Request</p>
          </div>
          
          <div style="background: #ffffff; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 10px 10px;">
            <h2 style="color: #111827; margin-top: 0;">Reset Your Password</h2>
            
            <p style="color: #4b5563; font-size: 16px;">
              We received a request to reset your password for your Kings Park POS account. Click the button below to create a new password.
            </p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${resetLink}" 
                 style="display: inline-block; background: #ec4899; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;">
                Reset Password
              </a>
            </div>
            
            <p style="color: #6b7280; font-size: 14px; margin-top: 30px;">
              This link will expire in 1 hour. If you didn't request a password reset, please ignore this email or contact your administrator if you have concerns.
            </p>
            
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 30px 0;">
            
            <p style="color: #9ca3af; font-size: 12px; margin: 0;">
              If the button doesn't work, copy and paste this link into your browser:<br>
              <a href="${resetLink}" style="color: #ec4899; word-break: break-all;">${resetLink}</a>
            </p>
          </div>
          
          <div style="text-align: center; margin-top: 20px; color: #9ca3af; font-size: 12px;">
            <p>© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.</p>
          </div>
        </body>
      </html>
    `,
    text: `
Reset Your Password

We received a request to reset your password for your Kings Park POS account. Click the link below to create a new password:

${resetLink}

This link will expire in 1 hour. If you didn't request a password reset, please ignore this email.

© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.
    `.trim(),
  }),

  /**
   * Order receipt email template
   */
  orderReceipt: (order: any, customerName: string) => ({
    subject: `Your Order Receipt - ${order.orderType === 'dine-in' ? `Table ${order.tableNumber}` : order.orderType === 'takeaway' ? `Takeaway #${order.takeawayNumber}` : `Delivery #${order.deliveryNumber}`}`,
    html: `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Order Receipt</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #ec4899 0%, #db2777 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">KINGS PARK</h1>
            <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0 0;">Order Receipt</p>
          </div>
          
          <div style="background: #ffffff; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 10px 10px;">
            <p style="color: #4b5563; font-size: 16px; margin-top: 0;">
              Thank you for your order, ${customerName}!
            </p>
            
            <div style="background: #f9fafb; padding: 20px; border-radius: 6px; margin: 20px 0;">
              <h3 style="color: #111827; margin-top: 0;">Order Details</h3>
              <p style="margin: 5px 0;"><strong>Order ID:</strong> ${order.id}</p>
              <p style="margin: 5px 0;"><strong>Type:</strong> ${order.orderType}</p>
              ${order.tableNumber ? `<p style="margin: 5px 0;"><strong>Table:</strong> ${order.tableNumber}</p>` : ''}
              <p style="margin: 5px 0;"><strong>Date:</strong> ${new Date(order.timestamp).toLocaleString()}</p>
            </div>
            
            <h3 style="color: #111827;">Items</h3>
            <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
              <thead>
                <tr style="border-bottom: 2px solid #e5e7eb;">
                  <th style="text-align: left; padding: 10px; color: #6b7280; font-weight: 600;">Item</th>
                  <th style="text-align: center; padding: 10px; color: #6b7280; font-weight: 600;">Qty</th>
                  <th style="text-align: right; padding: 10px; color: #6b7280; font-weight: 600;">Price</th>
                </tr>
              </thead>
              <tbody>
                ${order.items.map((item: any) => `
                  <tr style="border-bottom: 1px solid #f3f4f6;">
                    <td style="padding: 10px;">
                      ${item.name}
                      ${item.selectedMeatOption ? `<br><span style="color: #ec4899; font-size: 12px;">${item.selectedMeatOption}</span>` : ''}
                    </td>
                    <td style="text-align: center; padding: 10px;">${item.quantity}</td>
                    <td style="text-align: right; padding: 10px;">$${(item.price * item.quantity).toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div style="border-top: 2px solid #e5e7eb; padding-top: 20px; margin-top: 20px;">
              <div style="display: flex; justify-content: space-between; margin: 10px 0;">
                <span style="color: #6b7280;">Subtotal:</span>
                <span style="font-weight: 600;">$${order.subtotal.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 10px 0;">
                <span style="color: #6b7280;">Tax (15%):</span>
                <span style="font-weight: 600;">$${order.taxAmount.toFixed(2)}</span>
              </div>
              ${order.discountAmount ? `
              <div style="display: flex; justify-content: space-between; margin: 10px 0; color: #10b981;">
                <span>Discount:</span>
                <span style="font-weight: 600;">-$${order.discountAmount.toFixed(2)}</span>
              </div>
              ` : ''}
              <div style="display: flex; justify-content: space-between; margin: 20px 0; padding-top: 20px; border-top: 2px solid #e5e7eb;">
                <span style="font-size: 18px; font-weight: bold; color: #111827;">Total:</span>
                <span style="font-size: 18px; font-weight: bold; color: #ec4899;">$${order.totalAmount.toFixed(2)}</span>
              </div>
            </div>
            
            ${order.notes ? `
            <div style="background: #fef3c7; padding: 15px; border-radius: 6px; margin-top: 20px;">
              <p style="margin: 0; color: #92400e;"><strong>Notes:</strong> ${order.notes}</p>
            </div>
            ` : ''}
          </div>
          
          <div style="text-align: center; margin-top: 20px; color: #9ca3af; font-size: 12px;">
            <p>Thank you for dining with us!</p>
            <p>© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.</p>
          </div>
        </body>
      </html>
    `,
    text: `
Order Receipt

Thank you for your order, ${customerName}!

Order ID: ${order.id}
Type: ${order.orderType}
${order.tableNumber ? `Table: ${order.tableNumber}\n` : ''}
Date: ${new Date(order.timestamp).toLocaleString()}

Items:
${order.items.map((item: any) => `- ${item.name}${item.selectedMeatOption ? ` (${item.selectedMeatOption})` : ''} x${item.quantity} - $${(item.price * item.quantity).toFixed(2)}`).join('\n')}

Subtotal: $${order.subtotal.toFixed(2)}
Tax (15%): $${order.taxAmount.toFixed(2)}
${order.discountAmount ? `Discount: -$${order.discountAmount.toFixed(2)}\n` : ''}Total: $${order.totalAmount.toFixed(2)}

${order.notes ? `Notes: ${order.notes}\n` : ''}
Thank you for dining with us!

© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.
    `.trim(),
  }),

  /**
   * Reservation confirmation email template
   */
  reservationConfirmation: (params: {
    customerName: string;
    reservationDate: string;
    reservationTime: string;
    partySize: number;
    adultsCount?: number;
    kidsCount?: number;
    kidsBelow10Count?: number;
    kidsAbove10Count?: number;
    tableInfo?: string;
    eventName?: string;
    omitTableFromCustomerComms?: boolean;
    parkingInstructions?: string;
    slotLabel?: string;
    allergySummary?: string;
    bookingHeroImageUrl?: string;
    logoUrl?: string;
    businessName?: string;
  }) => {
    const esc = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const safeImg = (url: string | undefined) => {
      const u = url?.trim() ?? '';
      const low = u.toLowerCase();
      if (!low.startsWith('https://') && !low.startsWith('http://')) return undefined;
      return u;
    };
    const partyLine = (() => {
      if (params.kidsBelow10Count != null || params.kidsAbove10Count != null) {
        const kb = params.kidsBelow10Count ?? 0;
        const ka = params.kidsAbove10Count ?? 0;
        const ac =
          params.adultsCount != null
            ? params.adultsCount
            : Math.max(0, params.partySize - kb - ka);
        return `${ac} adult${ac !== 1 ? 's' : ''}, ${kb} child${kb !== 1 ? 'ren' : ''} under 10, ${ka} aged 10+ (${params.partySize} guests)`;
      }
      if (params.adultsCount != null && params.kidsCount != null) {
        return `${params.adultsCount} adult${params.adultsCount !== 1 ? 's' : ''}, ${params.kidsCount} child${params.kidsCount !== 1 ? 'ren' : ''}`;
      }
      return String(params.partySize);
    })();
    const showTable =
      !params.omitTableFromCustomerComms && Boolean(params.tableInfo?.trim());
    const tableBlockHtml = params.omitTableFromCustomerComms
      ? '<p style="margin: 5px 0;color:#6b7280;font-size:14px;">We will confirm seating separately; your table may change for operational reasons.</p>'
      : showTable
        ? `<p style="margin: 5px 0;"><strong>Table(s):</strong> ${esc(params.tableInfo!)}</p>`
        : '';
    const parkingTrimmed = params.parkingInstructions?.trim();
    const parkingCalloutHtml = parkingTrimmed
      ? `
    <div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:16px 18px;margin:24px 0 0;">
      <p style="margin:0 0 8px;font-size:13px;font-weight:700;color:#1e3a8a;text-transform:uppercase;letter-spacing:0.04em;">Parking</p>
      <p style="margin:0;color:#1e40af;font-size:15px;line-height:1.55;">${esc(parkingTrimmed).replace(/\n/g, '<br>')}</p>
    </div>`
      : '';
    const heroUrl = safeImg(params.bookingHeroImageUrl);
    const logoUrlSafe = safeImg(params.logoUrl);
    const headline =
      params.eventName?.trim() || params.businessName?.trim() || 'Kings Park';
    const logoAlt = params.businessName?.trim() ? `${esc(params.businessName.trim())} logo` : 'Restaurant logo';
    const logoRowHtml =
      logoUrlSafe != null
        ? `<div style="text-align:right;margin:0 0 18px;">
          <img src="${esc(logoUrlSafe)}" alt="${logoAlt}" width="200" height="56" style="max-height:56px;width:auto;height:auto;display:inline-block;border:0;" />
        </div>`
        : '';
    const heroBlockHtml = heroUrl
      ? `
        <div style="border-radius:10px 10px 0 0;overflow:hidden;border:1px solid #e5e7eb;border-bottom:0;background:#18181b;">
          <img src="${esc(heroUrl)}" alt="" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;line-height:0;" />
          <div style="padding:16px 22px;background:rgba(24,24,27,0.92);">
            <p style="margin:0;color:#ffffff;font-size:22px;font-weight:700;line-height:1.25;">${esc(headline)}</p>
            <p style="margin:6px 0 0;color:rgba(255,255,255,0.88);font-size:14px;">Reservation confirmed</p>
          </div>
        </div>`
      : `
        <div style="background:linear-gradient(135deg,#db2777 0%,#e11d48 50%,#be185d 100%);padding:28px 24px;text-align:left;border-radius:10px 10px 0 0;border:1px solid #e5e7eb;border-bottom:0;">
          <p style="margin:0;color:#ffffff;font-size:24px;font-weight:700;line-height:1.25;">${esc(headline)}</p>
          <p style="margin:8px 0 0;color:rgba(255,255,255,0.9);font-size:15px;">Reservation confirmed</p>
        </div>`;
    return {
    subject: `Reservation Confirmed – ${params.reservationDate} at ${params.reservationTime}`,
    html: `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Reservation Confirmed</title>
        </head>
        <body style="margin:0;padding:20px 12px;background:#f4f4f5;font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#333;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="max-width:600px;width:100%;margin:0 auto;">
          <tr><td style="padding:0;">
            ${heroBlockHtml}
            <div style="background:#ffffff;padding:28px 24px;border:1px solid #e5e7eb;border-top:0;border-radius:0 0 10px 10px;">
              ${logoUrlSafe ? logoRowHtml : ''}
            <h2 style="color:#111827;margin:0 0 8px;font-size:20px;">Hi ${esc(params.customerName)},</h2>
            <p style="color:#4b5563;font-size:16px;margin:0 0 20px;">Your reservation has been confirmed.</p>
            <div style="background:#f9fafb;padding:20px;border-radius:8px;border:1px solid #e5e7eb;">
              <p style="margin:5px 0;"><strong>Date:</strong> ${esc(params.reservationDate)}</p>
              <p style="margin:5px 0;"><strong>Time:</strong> ${esc(params.reservationTime)}</p>
              ${params.slotLabel ? `<p style="margin:5px 0;"><strong>Sitting:</strong> ${esc(params.slotLabel)}</p>` : ''}
              <p style="margin:5px 0;"><strong>Party:</strong> ${partyLine}</p>
              ${tableBlockHtml}
              ${params.eventName ? `<p style="margin:5px 0;"><strong>Event:</strong> ${esc(params.eventName)}</p>` : ''}
              ${params.allergySummary ? `<p style="margin:5px 0;"><strong>Allergy note:</strong> ${esc(params.allergySummary)}</p>` : ''}
            </div>
            ${parkingCalloutHtml}
            <p style="color:#6b7280;font-size:14px;margin:24px 0 0;">We look forward to seeing you!</p>
            </div>
            <p style="text-align:center;margin:20px 0 0;color:#9ca3af;font-size:12px;">© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.</p>
          </td></tr>
        </table>
        </body>
      </html>
    `.trim(),
    text: `
Hi ${params.customerName},

Your reservation has been confirmed.

Date: ${params.reservationDate}
Time: ${params.reservationTime}
${params.slotLabel ? `Sitting: ${params.slotLabel}\n` : ''}Party: ${partyLine}
${params.omitTableFromCustomerComms ? 'Seating will be confirmed separately.\n' : showTable ? `Table(s): ${params.tableInfo}\n` : ''}${params.eventName ? `Event: ${params.eventName}\n` : ''}${params.allergySummary ? `Allergy note: ${params.allergySummary}\n` : ''}${parkingTrimmed ? `\nParking:\n${parkingTrimmed}\n` : ''}
We look forward to seeing you!

© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.
    `.trim(),
  };
  },

  /**
   * Reservation waitlist notification
   */
  reservationWaitlist: (params: {
    customerName: string;
    reservationDate: string;
    reservationTime: string;
    partySize: number;
    adultsCount?: number;
    kidsCount?: number;
    eventName?: string;
  }) => {
    const partyLine =
      params.adultsCount != null && params.kidsCount != null
        ? `${params.adultsCount} adult${params.adultsCount !== 1 ? 's' : ''}, ${params.kidsCount} child${params.kidsCount !== 1 ? 'ren' : ''}`
        : `${params.partySize}`;
    return ({
    subject: `You're on the waiting list – ${params.reservationDate} at ${params.reservationTime}`,
    html: `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Waiting List</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #ec4899 0%, #db2777 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">KINGS PARK</h1>
            <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0 0;">Waiting List</p>
          </div>
          <div style="background: #ffffff; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 10px 10px;">
            <h2 style="color: #111827; margin-top: 0;">Hi ${params.customerName},</h2>
            <p style="color: #4b5563; font-size: 16px;">You have been added to our waiting list for the requested date and time. We will notify you by email if a table becomes available.</p>
            <div style="background: #f9fafb; padding: 20px; border-radius: 6px; margin: 20px 0;">
              <p style="margin: 5px 0;"><strong>Date:</strong> ${params.reservationDate}</p>
              <p style="margin: 5px 0;"><strong>Time:</strong> ${params.reservationTime}</p>
              <p style="margin: 5px 0;"><strong>Party:</strong> ${partyLine}</p>
              ${params.eventName ? `<p style="margin: 5px 0;"><strong>Event:</strong> ${params.eventName}</p>` : ''}
            </div>
          </div>
          <div style="text-align: center; margin-top: 20px; color: #9ca3af; font-size: 12px;">
            <p>© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.</p>
          </div>
        </body>
      </html>
    `.trim(),
    text: `
Hi ${params.customerName},

You have been added to our waiting list. We will notify you if a table becomes available.

Date: ${params.reservationDate}
Time: ${params.reservationTime}
Party: ${partyLine}
${params.eventName ? `Event: ${params.eventName}\n` : ''}
© ${new Date().getFullYear()} Kings Park Restaurant. All rights reserved.
    `.trim(),
  });
  },
};

export function logEmailSendFailure(result: { error?: string; skipped?: boolean }, label: string) {
  if (result.skipped) {
    console.warn(`${label} skipped (Resend not configured):`, result.error);
  } else {
    console.error(`${label}:`, result.error);
  }
}

/**
 * Email Service
 */
export const emailService = {
  /**
   * Send staff invitation email
   */
  async sendStaffInvitation(email: string, staffName: string, resetLink: string): Promise<{ success: boolean; error?: string }> {
    try {
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'staff-invitation',
          email,
          data: {
            staffName,
            resetLink,
          },
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        logEmailSendFailure(result, 'Staff invitation email');
        return { success: false, error: result.error || 'Failed to send email' };
      }
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error sending staff invitation email:', error);
      return { success: false, error: error.message || 'Failed to send email' };
    }
  },

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string, resetLink: string): Promise<{ success: boolean; error?: string }> {
    try {
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'password-reset',
          email,
          data: {
            resetLink,
          },
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        logEmailSendFailure(result, 'Password reset email');
        return { success: false, error: result.error || 'Failed to send email' };
      }
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error sending password reset email:', error);
      return { success: false, error: error.message || 'Failed to send email' };
    }
  },

  /**
   * Send order receipt email
   */
  async sendOrderReceipt(email: string, order: any, customerName: string, businessInfo?: any, loyaltyPoints?: number): Promise<{ success: boolean; error?: string }> {
    try {
      const functionUrl = getFunctionUrl();
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'order-receipt',
          email,
          data: {
            order,
            customerName,
            businessInfo,
            loyaltyPoints: loyaltyPoints || 0,
          },
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        logEmailSendFailure(result, 'Order receipt email');
        return { success: false, error: result.error || 'Failed to send email' };
      }
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error sending order receipt email:', error);
      return { success: false, error: error.message || 'Failed to send email' };
    }
  },

  /**
   * Send reservation confirmation email
   */
  async sendReservationConfirmation(
    email: string,
    params: {
      customerName: string;
      reservationDate: string;
      reservationTime: string;
      partySize: number;
      adultsCount?: number;
      kidsCount?: number;
      kidsBelow10Count?: number;
      kidsAbove10Count?: number;
      tableInfo?: string;
      eventName?: string;
      omitTableFromCustomerComms?: boolean;
      parkingInstructions?: string;
      slotLabel?: string;
      allergySummary?: string;
      bookingHeroImageUrl?: string;
      logoUrl?: string;
      businessName?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'reservation-confirmation',
          email,
          data: params,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        logEmailSendFailure(result, 'Reservation confirmation email');
        return { success: false, error: result.error || 'Failed to send email' };
      }
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send email' };
    }
  },

  /**
   * Send reservation waitlist notification
   */
  async sendReservationWaitlist(
    email: string,
    params: {
      customerName: string;
      reservationDate: string;
      reservationTime: string;
      partySize: number;
      adultsCount?: number;
      kidsCount?: number;
      eventName?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const functionUrl = getFunctionUrl();
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'reservation-waitlist',
          email,
          data: params,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        logEmailSendFailure(result, 'Reservation waitlist email');
        return { success: false, error: result.error || 'Failed to send email' };
      }
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to send email' };
    }
  },

  /**
   * Send staff payslip email.
   * Uses Firebase callable (sendEmailCallable) to avoid CORS when app is on custom domain (e.g. pos.kingspark.co.nz).
   */
  async sendStaffPayslip(
    email: string,
    data: {
      staffName: string;
      payPeriodStart: string;
      payPeriodEnd: string;
      hours: number;
      hourlyRate: number;
      holidayPay?: number;
      gross: number;
      paye: number;
      net: number;
      businessInfo?: any;
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const functions = getFirebaseFunctions();
      const sendEmailCallable = httpsCallable<{ type: string; email: string; data: typeof data }, { success: boolean; id?: string }>(functions, 'sendEmailCallable');
      const result = await sendEmailCallable({ type: 'staff-payslip', email, data });

      if (result.data?.success) {
        console.log('✅ Staff payslip email sent successfully:', result.data.id);
        return { success: true };
      }
      return { success: false, error: 'Failed to send email' };
    } catch (error: any) {
      console.error('❌ Error sending staff payslip email:', error);
      const message = error?.message || error?.code || 'Failed to send email';
      return { success: false, error: message };
    }
  },
};

